import React from 'react';
import BigNumber from 'bignumber.js';
import { Box, Tooltip, Typography } from '@mui/material';
import { displayTokenAmount } from '../../utils/displayTokenAmount';


export interface IRangeIndicatorProps {
  lowerPosition: BigNumber;
  upperPosition: BigNumber;
  currentPrice: BigNumber;
  symbolTokenA: string;
  symbolTokenB: string;
}

export const DetailsLine = ({ label, value, postfix }: { label: string; value: BigNumber; postfix: string; }) => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'space-between',
    gap: 4,
  }}>
    <Typography
      variant="main14"
      color="text.secondary"
      sx={{
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="main14"
      noWrap
    >
      {displayTokenAmount(value)}{' '}
      {postfix}
    </Typography>
  </Box>
)

export const RangeIndicator = ({
  lowerPosition,
  upperPosition,
  currentPrice,
  symbolTokenA,
  symbolTokenB,
}: IRangeIndicatorProps) => {
  const outOfRange = currentPrice.isLessThan(lowerPosition) || currentPrice.isGreaterThan(upperPosition)
  const priceIndent = upperPosition.minus(lowerPosition).div(70).multipliedBy(15);
  const lowerVisiblePrice = lowerPosition.minus(priceIndent);
  const upperVisiblePrice = upperPosition.plus(priceIndent);

  const leftOffset = currentPrice.minus(lowerVisiblePrice)
    .div(upperVisiblePrice.minus(lowerVisiblePrice))
    .decimalPlaces(2);

  const leftOffsetPercent = leftOffset.isLessThan(0)
    ? BigNumber(0)
    : leftOffset.isGreaterThan(1)
      ? BigNumber(100)
      : leftOffset.multipliedBy(100);

  return (
    <Box
      sx={{
        py: '4px',
      }}
    >
      <Tooltip
        title={(
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            pb: 2,
          }}>
            <DetailsLine
              label="Min Price"
              value={lowerPosition}
              postfix={`${symbolTokenA} per ${symbolTokenB}`}
            />
            <DetailsLine
              label="Max Price"
              value={upperPosition}
              postfix={`${symbolTokenA} per ${symbolTokenB}`}
            />
            <DetailsLine
              label="Current Price"
              value={currentPrice}
              postfix={`${symbolTokenA} per ${symbolTokenB}`}
            />
          </Box>
        )}
        placement="bottom-end"
        arrow
      >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '6px',
          px: '15%',
          backgroundColor: '#2A2A32',
          border: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '2px',
        }}
      >
        {currentPrice.isPositive() && lowerPosition.isPositive() && upperPosition.isPositive() && (
          <>
            <Box
              sx={{
                width: '100%',
                height: '6px',
                backgroundColor: outOfRange ? '#F01952' : '#95CC16',
                borderRadius: '2px',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: `calc(${leftOffsetPercent}% - 2px)`,
                top: '-4px',
                width: '4px',
                height: '14px',
                borderRadius: '2px',
                backgroundColor: '#CCCCCC',
                border: '1px solid rgb(24, 24, 27)'
              }}
            />
          </>
        )}
      </Box>

      </Tooltip>
    </Box>
  );
};
